import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import InfoIcon from "../assets/images/Info-icon.svg";
import {
  setAddAmountSIP,
  setAge,
  setApproxReturn,
  setIncrementPerYear,
  setIsOpen,
  setMonthlyInvestment,
  setNoOfInvestementYear,
  setSipErrors,
  setSwpStartYear,
  setYearlyIncrementInWithdrawSwp,
  setYearlyWithdraw,
} from "../redux/calculations/slice";
import { ButtonOutlined } from "./ButtonOutlined";
import { Button } from "./Button";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import { useLocation } from "react-router-dom";
import Input from "./Input";
import useLocale from "../../src/locales/index";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import DecimalUtils from "../utiles/decimalFormate";
import Switch from "react-switch";

const SipAndSwp = ({
  activeTab,
  setShowTable,
  generateData,
  resetAllValues,
  scrollToResult
}) => {
  const { textData } = useLocale();
  const sipLumpsumCalc = textData.sipLumpsumCalc;
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const demoRef = useRef();
  const [pageKey, setPageKey] = useState(0);
  const location = useLocation();
  const [existingData, setExistingData] = useState(
    JSON.parse(localStorage.getItem(textData.storeData)) || []
  );
  const [showSWP, setShowSWP] = useState(true);
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const {
    age,
    monthlyInvestment,
    incrementPerYear,
    approxReturn,
    noOfInvestementYear,
    swpStartYear,
    yearlyWithdraw,
    yearlyIncrementInWithdrawSwp,
    addAmountSIP,
    investmentAmounts,
  } = useSelector((state) => state.sip);

  const totalFreeAmount = investmentAmounts?.reduce(
    (accumulator, currentValue) => accumulator + Number(currentValue),
    0
  );

  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const openModal = () => {
    dispatch(setIsOpen(true));
  };

  const handleAddButtonClick = () => {
    setShowTable(false);
    openModal();
    dispatch(setAddAmountSIP(true));
  };

  const getFormattedPlaceholder = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  const timeLabel = t("yearShort");

  const validationSchema = Yup.object().shape({
    age: Yup.number()
      .typeError(sipLumpsumCalc.ageNumber)
      .required(sipLumpsumCalc.ageRequired)
      .min(1, sipLumpsumCalc.ageMin)
      .max(99, sipLumpsumCalc.ageMax),
    monthlyInvestment: Yup.number()

      .required(sipLumpsumCalc.monthlyInvestmentRequired)
      .typeError(sipLumpsumCalc.monthlyInvestmentNumber)
      .min(1000, sipLumpsumCalc.monthlyInvestmentMin)
      .max(500000, sipLumpsumCalc.monthlyInvestmentMax),
    incrementPerYear: Yup.number()
      .required(sipLumpsumCalc.incrementPerYearNumber)
      .min(0, sipLumpsumCalc.incrementPerYearMin)
      .max(50, sipLumpsumCalc.incrementPerYearMax),
    approxReturn: Yup.number()
      .required(sipLumpsumCalc.approxReturnRequired)
      .typeError(sipLumpsumCalc.approxReturnNumber)
      .min(4, sipLumpsumCalc.approxReturnMin)
      .max(20, sipLumpsumCalc.approxReturnMax),
    noOfInvestementYear: Yup.number()
      .required(sipLumpsumCalc.noOfInvestementYearRequired)
      .typeError(sipLumpsumCalc.noOfInvestementYearNumber)
      .min(1, sipLumpsumCalc.noOfInvestementYearMin)
      .max(45, sipLumpsumCalc.noOfInvestementYearMax),
    swpStartYear: Yup.number()
      .required(sipLumpsumCalc.swpStartYearNumber)
      .min(1, sipLumpsumCalc.swpStartYearMin)
      .max(45, sipLumpsumCalc.swpStartYearMax),
    yearlyWithdraw: Yup.number()
      .typeError(sipLumpsumCalc.yearlyWithdrawNumber)
      .min(0, sipLumpsumCalc.yearlyWithdrawMin)
      .max(5000000, sipLumpsumCalc.yearlyWithdrawMax),
    yearlyIncrementInWithdrawSwp: Yup.number()
      .required(sipLumpsumCalc.yearlyIncrementInWithdrawSwpNumber)
      .min(0, sipLumpsumCalc.yearlyIncrementInWithdrawSwpMin)
      .max(100, sipLumpsumCalc.yearlyIncrementInWithdrawSwpMax),
  });

  useEffect(() => {
    resetAllValues();
    setPageKey((prevKey) => prevKey + 1);
  }, [location.pathname]);

  const ageSliderRef = useRef(null);
  const incrementSliderRef = useRef(null);
  const returnSliderRef = useRef(null);
  const investmentYearSliderRef = useRef(null);
  const monthlyInvestmentSliderRef = useRef(null);
  const swpStartYearSliderRef = useRef(null);
  const yearlyWithdrawSliderRef = useRef(null);
  const yearlyIncrementWithdrawSliderRef = useRef(null);
  const noInvestRef = useRef(null);

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Formik
        key={pageKey}
        innerRef={demoRef}
        initialValues={{
          age: age.toString(),
          monthlyInvestment: monthlyInvestment.toString(),
          incrementPerYear: incrementPerYear.toString(),
          approxReturn: approxReturn.toString(),
          noOfInvestementYear: noOfInvestementYear.toString(),
          swpStartYear: swpStartYear.toString(),
          yearlyWithdraw: yearlyWithdraw.toString(),
          yearlyIncrementInWithdrawSwp: yearlyIncrementInWithdrawSwp.toString(),
        }}
        validateOnBlur
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const data = await generateData();
          if (cookieAccept && analytics) {
            logEvent(analytics, "calculate_SIP");
          }
          // cookieAccept && logEvent(analytics, `calculate_SIP`);
          const isDuplicate = existingData?.some((item) => {
            return JSON.stringify(item) === JSON.stringify(data);
          });

          if (!isDuplicate) {
            const updatedData = [...existingData, data];
            if (updatedData.length > 20) {
              updatedData.shift();
            }
            setExistingData(updatedData);
            localStorage.setItem(
              textData.storeData,
              JSON.stringify(updatedData)
            );
          }
          setShowTable(false);
          scrollToResult();
        }}
      >
        {(formik) => {
          const { errors, touched, handleSubmit, handleBlur, setFieldValue } =
            formik;
          dispatch(setSipErrors(errors));
          const handleIncremenetChange = (e) => {
            const rawValue = e.target.value;
            if (rawValue === "") {
              formik.setFieldValue("incrementPerYear", rawValue);
              dispatch(setIncrementPerYear(""));
              return;
            }
            if (rawValue.length > 5) return;
            if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
            const normalizedValue = DecimalUtils.normalizeInput(rawValue);

            formik.setFieldValue("incrementPerYear", normalizedValue);
            dispatch(setIncrementPerYear(normalizedValue));
          };

          const handleReturnRateChange = (e) => {
            const rawValue = e.target.value;
            if (rawValue === "") {
              formik.setFieldValue("approxReturn", rawValue);
              dispatch(setApproxReturn(""));
              return;
            }
            if (rawValue.length > 5) return;
            if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
            const normalizedValue = DecimalUtils.normalizeInput(rawValue);

            formik.setFieldValue("approxReturn", normalizedValue);
            dispatch(setApproxReturn(normalizedValue));
          };

          const handleWithdrawSwpChange = (e) => {
            const rawValue = e.target.value;
            if (rawValue === "") {
              formik.setFieldValue("yearlyIncrementInWithdrawSwp", rawValue);
              dispatch(setYearlyIncrementInWithdrawSwp(""));
              return;
            }
            if (rawValue.length > 5) return;
            if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
            const normalizedValue = DecimalUtils.normalizeInput(rawValue);

            formik.setFieldValue(
              "yearlyIncrementInWithdrawSwp",
              normalizedValue
            );
            dispatch(setYearlyIncrementInWithdrawSwp(normalizedValue));
          };

          return (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              {/* Age Input */}
              <div>
                <div className="flex justify-between items-center gap-4">
                  <label className="text-start font-BlinkMacSystemFont font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.sipCalc.age}
                  </label>
                </div>

                <div className="flex justify-between gap-5 items-center">
                  <div className="relative w-full" ref={ageSliderRef}>

                    <input
                      type="range"
                      min={1}
                      max={99}
                      step={1}
                      value={age}
                      onChange={(e) => {
                        setFieldValue("age", e.target.value);
                        dispatch(setAge(e.target.value));
                      }}
                      className="
                        flex-1
                        min-w-0
                        max-w-[314px]
                      "
                      style={{
                        width: "100%",
                        appearance: "none",
                        background: "transparent",
                        ...(isRTL() && {
                          direction: "ltr",
                        }),
                        "--val": `${((age - 1) / 98) * 100}%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center flex-col">
                    <Input
                      name="age"
                      leftIcon={timeLabel}
                      value={age}
                      type="number"
                      onBlur={handleBlur}
                      placeholder="24"
                      onChange={(e) => {
                        if (/^\d{0,2}$/.test(e.target.value)) {
                          setFieldValue("age", e.target.value);
                          dispatch(setAge(e.target.value));
                        }
                      }}
                      borderColor={
                        errors.age && touched.age
                          ? "border-red-500"
                          : "border-gray-light"
                      }
                      className={`" sm:h-[36px] h-[32px]    border ${
                        errors.age && touched.age
                          ? "border-danger focus:border-red-500"
                          : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                    />
                    {errors.age && touched.age && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {errors.age}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Monthly Investment Input */}
              <div>
                <div className="flex justify-between items-center mb-2 gap-4">
                  <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.sipCalc.monthlyInvestment}
                  </label>
                </div>
                <div className="flex justify-between gap-5 items-center">
                  <div
                    className="relative w-full"
                    ref={monthlyInvestmentSliderRef}
                  >

                    <input
                      type="range"
                      min={1000}
                      max={500000}
                      step={100}
                      value={
                        typeof monthlyInvestment === "string"
                          ? parseFloat(monthlyInvestment.replace(/,/g, "")) || 0
                          : monthlyInvestment
                      }
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setFieldValue("monthlyInvestment", value);
                        dispatch(setMonthlyInvestment(value));
                      }}
                      className="
                        flex-1
                        min-w-0
                        max-w-[314px]
                      "
                      style={{
                        width: "100%",
                        appearance: "none",
                        background: "transparent",
                        ...(isRTL() && {
                          direction: "ltr",
                        }),
                        "--val":
                          monthlyInvestment && !isNaN(monthlyInvestment)
                            ? `${((monthlyInvestment - 1000) / (500000 - 1000)) *
                            100
                            }%`

                            : "0%",
                        //   (((typeof monthlyInvestment === "string"
                        //     ? parseFloat(monthlyInvestment.replace(/,/g, ""))
                        //     : monthlyInvestment) -
                        //     1000) /
                        //     (500000 - 1000)) *
                        //   100
                        // }%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center flex-col">
                    <Input
                      name="monthlyInvestment"
                      leftIcon={textData.currency}
                      value={
                        typeof monthlyInvestment === "string"
                          ? monthlyInvestment
                          : formatToINR(monthlyInvestment)
                      }
                      type="text"
                      dir="ltr"
                      onBlur={handleBlur}
                      placeholder={getFormattedPlaceholder(
                        10000,
                        i18n.language
                      )}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/,/g, "");
                        const numericValue = parseFloat(
                          rawValue
                            .replace(/,/g, ".")
                            .replace(/\./g, "")
                            .replace(/(\d+)(\.\d{2})?/, "$1.$2")
                        ); // Convert to a number
                        setFieldValue("monthlyInvestment", numericValue);
                        dispatch(setMonthlyInvestment(numericValue));
                      }}
                      borderColor={
                        errors.monthlyInvestment && touched.monthlyInvestment
                          ? "border-red-500"
                          : "border-gray-light"
                      }
                      className={`" sm:h-[36px] h-[32px]    border ${
                        errors.monthlyInvestment && touched.monthlyInvestment
                          ? "border-danger focus:border-red-500"
                          : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                    />
                    {errors.monthlyInvestment && touched.monthlyInvestment && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {errors.monthlyInvestment}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Increment Per Year Input */}
              <div>
                <div className="flex justify-between items-center mb-2 gap-4">
                  <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.sipCalc.incrementPerYear}
                  </label>
                </div>
                <div className="flex justify-between gap-5 items-center">
                  <div className="relative w-full" ref={incrementSliderRef}>

                    <input
                      type="range"
                      min={0}
                      max={50}
                      step={0.5}
                      value={parseFloat(incrementPerYear)}
                      onChange={(e) => {
                        setFieldValue("incrementPerYear", e.target.value);
                        dispatch(setIncrementPerYear(e.target.value));
                      }}
                      className="
                        flex-1
                        min-w-0
                        max-w-[314px]
                      "
                      style={{
                        width: "100%",
                        appearance: "none",
                        background: "transparent",
                        ...(isRTL() && {
                          direction: "ltr",
                        }),
                        "--val": `${((incrementPerYear - 0) / (50 - 0)) * 100
                          }%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center flex-col">
                    <Input
                      name="incrementPerYear"
                      leftIcon="%"
                      type="text"
                      placeholder={DecimalUtils.getPlaceholder("6.5")}
                      value={DecimalUtils.formatDisplay(
                        formik.values.incrementPerYear
                      )}
                      onChange={handleIncremenetChange}
                      onBlur={handleBlur}
                      borderColor={
                        errors.incrementPerYear && touched.incrementPerYear
                          ? "border-red-500"
                          : "border-gray-light"
                      }
                      className={`" sm:h-[36px] h-[32px]    border ${
                        errors.incrementPerYear && touched.incrementPerYear
                          ? "border-danger focus:border-red-500"
                          : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                    />
                    {errors.incrementPerYear && touched.incrementPerYear && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {errors.incrementPerYear}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Approx. Return Input */}
              <div>
                <div className="flex justify-between items-center mb-2 gap-4">
                  <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.sipCalc.approxReturn}
                  </label>
                </div>
                <div className="flex justify-between gap-5 items-center">
                  <div className="relative w-full " ref={returnSliderRef}>

                    <input
                      type="range"
                      min={4}
                      max={20}
                      step={0.5}
                      value={parseFloat(approxReturn)}
                      onChange={(e) => {
                        setFieldValue("approxReturn", e.target.value);
                        dispatch(setApproxReturn(e.target.value));
                      }}
                      className="
                        flex-1
                        min-w-0
                        max-w-[314px]
                      "
                      style={{
                        width: "100%",
                        appearance: "none",
                        background: "transparent",
                        ...(isRTL() && {
                          direction: "ltr",
                        }),
                        "--val": `${((approxReturn - 4) / (20 - 4)) * 100}%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center flex-col">
                    <Input
                      name="approxReturn"
                      leftIcon="%"
                      type="text"
                      placeholder={DecimalUtils.getPlaceholder("6.5")}
                      value={DecimalUtils.formatDisplay(
                        formik.values.approxReturn
                      )}
                      onChange={handleReturnRateChange}
                      onBlur={handleBlur}
                      borderColor={
                        errors.approxReturn && touched.approxReturn
                          ? "border-danger"
                          : "border-gray-light"
                      }
                      className={` sm:h-[36px] h-[32px]    border ${
                        errors.approxReturn && touched.approxReturn
                          ? "border-danger focus:border-red-500"
                          : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                    />
                    {errors.approxReturn && touched.approxReturn && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {errors.approxReturn}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* No. of Investment year Input */}
              <div>
                <div className="flex justify-between items-center mb-2 gap-4">
                  <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.sipCalc.noOfInvestementYear}
                  </label>
                </div>

                <div className="flex justify-between gap-5 items-center">
                  <div
                    className="relative w-full"
                    ref={investmentYearSliderRef}
                  >

                    <input
                      type="range"
                      min={1}
                      max={45}
                      step={1}
                      value={noOfInvestementYear}
                      onChange={(e) => {
                        if (/^\d{0,2}$/.test(e.target.value)) {
                          setFieldValue("noOfInvestementYear", e.target.value);
                          dispatch(setNoOfInvestementYear(e.target.value));
                        }
                      }}
                      className="
                        flex-1
                        min-w-0
                        max-w-[314px]
                      "
                      style={{
                        width: "100%",
                        appearance: "none",
                        background: "transparent",
                        ...(isRTL() && {
                          direction: "ltr",
                        }),
                        "--val": `${((noOfInvestementYear - 1) / (45 - 1)) * 100
                          }%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center flex-col">
                    <Input
                      name="noOfInvestementYear"
                      leftIcon={timeLabel}
                      value={noOfInvestementYear}
                      type="number"
                      onBlur={handleBlur}
                      placeholder="12"
                      onChange={(e) => {
                        if (/^\d{0,2}$/.test(e.target.value)) {
                          setFieldValue("noOfInvestementYear", e.target.value);
                          dispatch(setNoOfInvestementYear(e.target.value));
                        }
                      }}
                      borderColor={
                        errors.noOfInvestementYear &&
                          touched.noOfInvestementYear
                          ? "border-red-500"
                          : "border-gray-light"
                      }
                      className={`" sm:h-[36px] h-[32px]    border ${
                        errors.noOfInvestementYear &&
                        touched.noOfInvestementYear
                          ? "border-danger focus:border-red-500"
                          : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                    />
                    {errors.noOfInvestementYear &&
                      touched.noOfInvestementYear && (
                        <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                          {errors.noOfInvestementYear}
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {/* Add Lumpsum Amount Section */}
              <div className="flex justify-between items-center mt-5 mb-5 p-3 border border-solid border-gray-light rounded-[6px] items-center">
                <div className="font-BlinkMacSystemFont font-medium text-black text-[15px] text-start">
                  {activeTab === 0 && addAmountSIP
                    ? textData.sipCalc.addFreeAmount
                    : textData.sipCalc.addLumpsum}
                  {activeTab === 0 && addAmountSIP && (
                    <button
                      disabled={errors.noOfInvestementYear ? true : false}
                      className="ml-2 text-blue-600 font-semibold sm:text-base 2xl:text-base text-[16px] text-[#0065FD]"
                      onClick={handleAddButtonClick}
                    >
                      {textData.sipCalc.edit}
                    </button>
                  )}
                </div>
                <div>
                  {activeTab === 0 && !addAmountSIP ? (
                    <ButtonOutlined
                      disabled={errors.noOfInvestementYear}
                      onClick={openModal}
                      className="sm:min-w-[112px] min-w-[100px]"
                    >
                      {textData.sipCalc.add}
                    </ButtonOutlined>
                  ) : (
                    activeTab === 0 &&
                    addAmountSIP && (
                      <ButtonOutlined
                        icon={textData.currency}
                        disabled={true}
                        className="sm:min-w-[112px] min-w-[100px]"
                      >
                        {totalFreeAmount ? totalFreeAmount : 0}
                      </ButtonOutlined>
                    )
                  )}
                </div>
              </div>
              <div className="border-b border-solid border-gray-light"></div>
              <div className="font-BlinkMacSystemFont font-medium text-black text-[15px] text-start flex justify-between">
                <span>{textData.sipCalc.addSWP}</span>
                <div className="scale-110">
                  <Switch
                    height={24}
                    width={44}
                    handleDiameter={20}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    onColor="#0D8787"
                    offColor="#D9D9D9"
                    checked={showSWP}
                    onChange={(checked) => setShowSWP(checked)} />
                </div>
              </div>
              {/* SWP Start Year */}
              {showSWP && (
                <>
                  <div>
                    <div className="flex justify-between items-center mb-2 gap-4">
                      <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm flex items-center gap-2">
                        {textData.sipCalc.swpStartYear}
                      </label>
                    </div>
                    <div className="flex justify-between gap-5 items-center">
                      <div className="relative w-full" ref={swpStartYearSliderRef}>

                        <input
                          type="range"
                          min={1}
                          max={45}
                          step={1}
                          value={swpStartYear}
                          onChange={(e) => {
                            setFieldValue("swpStartYear", e.target.value);
                            dispatch(setSwpStartYear(e.target.value));
                          }}
                          className="
                        flex-1
                        min-w-0
                        max-w-[314px]
                      "
                          style={{
                            width: "100%",
                            appearance: "none",
                            background: "transparent",
                            ...(isRTL() && {
                              direction: "ltr",
                            }),
                            "--val": `${((swpStartYear - 1) / (45 - 1)) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="flex items-center flex-col">
                        <Input
                          name="swpStartYear"
                          leftIcon={timeLabel}
                          value={swpStartYear}
                          type="number"
                          onBlur={handleBlur}
                          placeholder="10"
                          onChange={(e) => {
                            setFieldValue("swpStartYear", e.target.value);
                            dispatch(setSwpStartYear(e.target.value));
                          }}
                          borderColor={
                            errors.swpStartYear && touched.swpStartYear
                              ? "border-red-500"
                              : "border-gray-light"
                          }
                      className={`" sm:h-[36px] h-[32px]    border ${
                        errors.swpStartYear && touched.swpStartYear
                          ? "border-danger focus:border-red-500"
                          : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                        />
                        {errors.swpStartYear && touched.swpStartYear && (
                          <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                            {errors.swpStartYear}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Yearly Withdraw */}
                  <div>
                    <div className="flex justify-between items-center mb-2 gap-4">
                      <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm flex items-center gap-2">
                        {textData.sipCalc.yearlyWithdraw}

                        <div className="relative group">
                          <img
                            src={InfoIcon}
                            alt="info"
                            className="w-[22px] h-[22px] cursor-pointer"
                          />
                          <div className="absolute hidden -left-[112px] z-50  font-lato-regular-400 group-hover:flex justify-center leading-5 -top-[9px] -translate-y-full sm:w-[350px] w-[280px]  m-auto px-3 py-3 bg-gray rounded-lg text-center text-white text-[12px] after:content-[''] after:absolute  after:top-[100%] sm:after:-translate-x-[67px] after:-translate-x-[32px]  after:border-8 after:border-x-[transparent] after:border-b-[transparent] after:border-t-gray">
                            {textData.sipCalc.info}
                          </div>
                        </div>
                      </label>
                    </div>
                    <div className="flex justify-between gap-5 items-center">
                      <div
                        className="relative w-full"
                        ref={yearlyWithdrawSliderRef}
                      >

                        <input
                          type="range"
                          min={0}
                          max={5000000}
                          step={1000}
                          value={
                            typeof yearlyWithdraw === "string"
                              ? parseFloat(yearlyWithdraw.replace(/,/g, "")) || 0
                              : yearlyWithdraw
                          }
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setFieldValue("yearlyWithdraw", value);
                            dispatch(setYearlyWithdraw(formatToINR(value)));
                          }}
                          className="w-full appearance-none bg-transparent relative z-10"
                          style={{
                            width: "100%",
                            appearance: "none",
                            background: "transparent",
                            ...(isRTL() && {
                              direction: "ltr",
                            }),

                            "--val": (() => {
                              const numericValue =
                                typeof yearlyWithdraw === "string"
                                  ? parseFloat(yearlyWithdraw.replace(/,/g, "")) ||
                                  0
                                  : yearlyWithdraw || 0;
                              return `${((numericValue - 0) / (5000000 - 0)) * 100
                                }%`;
                            })(),
                          }}
                        />
                      </div>
                      <div className="flex items-center flex-col">
                        <Input
                          name="yearlyWithdraw"
                          leftIcon={textData.currency}
                          value={
                            typeof yearlyWithdraw === "string"
                              ? yearlyWithdraw
                              : formatToINR(yearlyWithdraw)
                          }
                          type="text"
                          dir="ltr"
                          onBlur={handleBlur}
                          placeholder={getFormattedPlaceholder(
                            100000,
                            i18n.language
                          )}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/,/g, "");
                            const numericValue = parseFloat(
                              rawValue
                                .replace(/,/g, ".")
                                .replace(/\./g, "")
                                .replace(/(\d+)(\.\d{2})?/, "$1.$2")
                            );
                            setFieldValue("yearlyWithdraw", numericValue);
                            dispatch(setYearlyWithdraw(numericValue));
                          }}
                          borderColor={
                            errors.yearlyWithdraw && touched.yearlyWithdraw
                              ? "border-red-500"
                              : "border-gray-light"
                          }
                      className={`" sm:h-[36px] h-[32px]    border ${
                        errors.yearlyWithdraw && touched.yearlyWithdraw
                          ? "border-danger focus:border-red-500"
                          : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                        />
                        {errors.yearlyWithdraw && touched.yearlyWithdraw && (
                          <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                            {errors.yearlyWithdraw}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Yearly Increment in Withdraw */}
                  <div>
                    <div className="flex justify-between items-center mb-2 gap-4">
                      <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                        {textData.sipCalc.yearlyIncrementInWithdrawSwp}
                      </label>
                    </div>

                    <div className="flex justify-between gap-5 items-center">
                      <div
                        className="relative w-full"
                        ref={yearlyIncrementWithdrawSliderRef}
                      >

                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={0.5}
                          value={parseFloat(yearlyIncrementInWithdrawSwp)}
                          onChange={(e) => {
                            setFieldValue(
                              "yearlyIncrementInWithdrawSwp",
                              e.target.value
                            );
                            dispatch(
                              setYearlyIncrementInWithdrawSwp(e.target.value)
                            );
                          }}
                          className="
                        flex-1
                        min-w-0
                        max-w-[314px]
                      "
                          style={{
                            width: "100%",
                            appearance: "none",
                            background: "transparent",
                            ...(isRTL() && {
                              direction: "ltr",
                            }),
                            "--val": `${((yearlyIncrementInWithdrawSwp - 0) / (100 - 0)) * 100
                              }%`,
                          }}
                        />
                      </div>
                      <div className="flex items-center flex-col">
                        <Input
                          name="yearlyIncrementInWithdrawSwp"
                          leftIcon="%"
                          type="text"
                          placeholder={DecimalUtils.getPlaceholder("6.5")}
                          value={DecimalUtils.formatDisplay(
                            formik.values.yearlyIncrementInWithdrawSwp
                          )}
                          onChange={handleWithdrawSwpChange}
                          onBlur={handleBlur}
                          borderColor={
                            errors.yearlyIncrementInWithdrawSwp &&
                              touched.yearlyIncrementInWithdrawSwp
                              ? "border-red-500"
                              : "border-gray-light"
                          }
                      className={`" sm:h-[36px] h-[32px]    border ${
                        errors.yearlyIncrementInWithdrawSwp &&
                        touched.yearlyIncrementInWithdrawSwp
                          ? "border-danger focus:border-red-500"
                          : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                        />
                        {errors.yearlyIncrementInWithdrawSwp &&
                          touched.yearlyIncrementInWithdrawSwp && (
                            <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                              {errors.yearlyIncrementInWithdrawSwp}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </>)}

              <div className="mt-[10px]">
                <Button type="submit" className="w-full">
                  {textData.calculate}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default SipAndSwp;
